import * as React from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus, Repeat, Square } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input, Label, NativeSelect } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { PageHeader } from "@/components/common";
import { api } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { parsePct } from "@/lib/money-input";

interface Policy {
  id: string;
  marketId: string;
  marketCode: string;
  name: string | null;
  categoryId: string | null;
  categoryName: string | null;
  serviceId: string | null;
  serviceName: string | null;
  freeCancelHours: number;
  cancellationFeeHours: number | null;
  cancellationFeeBps: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  state: "active" | "scheduled" | "ended";
}
interface Option {
  id: string;
  code: string;
  name: string;
}
interface Options {
  markets: (Option & { currency: string })[];
  categories: Option[];
  services: Option[];
}
interface MarketDefaults {
  markets: { id: string; code: string; settings: Record<string, unknown> | null }[];
}

const STATE: Record<Policy["state"], [string, "success" | "warning" | "secondary"]> = {
  active: ["Geçerli", "success"],
  scheduled: ["Planlandı", "warning"],
  ended: ["Bitti", "secondary"],
};

/** 168 → "7 gün", 36 → "36 saat" */
export function hoursText(h: number) {
  if (h >= 48 && h % 24 === 0) return `${h / 24} gün`;
  return `${h} saat`;
}
const num = (s: string) => (s.trim() === "" ? null : Number(s.replace(",", ".")));

function PolicyDialog({ options, replaces }: { options: Options; replaces?: Policy }) {
  const qc = useQueryClient();
  const inDays = Boolean(
    replaces && replaces.freeCancelHours >= 48 && replaces.freeCancelHours % 24 === 0,
  );
  const init = () => ({
    marketId: replaces?.marketId ?? options.markets[0]?.id ?? "",
    scope: (replaces?.serviceId ? "service" : "category") as "category" | "service",
    categoryId: replaces?.categoryId ?? "",
    serviceId: replaces?.serviceId ?? "",
    name: replaces?.name ?? "",
    free: replaces ? String(inDays ? replaces.freeCancelHours / 24 : replaces.freeCancelHours) : "",
    unit: (inDays ? "days" : "hours") as "hours" | "days",
    fee: replaces?.cancellationFeeHours?.toString().replace(".", ",") ?? "",
    pct:
      replaces?.cancellationFeeBps == null
        ? ""
        : String(replaces.cancellationFeeBps / 100).replace(".", ","),
    when: "now" as "now" | "later",
    at: "",
  });
  const [f, setF] = React.useState(init);
  const set = (p: Partial<ReturnType<typeof init>>) => setF((x) => ({ ...x, ...p }));
  const freeN = num(f.free);
  const freeHours = freeN === null ? null : f.unit === "days" ? freeN * 24 : freeN;
  const feeN = num(f.fee);
  const pctBps = f.pct.trim() ? parsePct(f.pct) : null;
  const at = f.when === "later" && f.at ? new Date(f.at) : null;
  const valid =
    freeHours !== null &&
    Number.isInteger(freeHours) &&
    freeHours >= 0 &&
    freeHours <= 2160 &&
    (feeN === null || (feeN >= 0 && feeN <= 8 && Number.isInteger(feeN * 4))) &&
    (!f.pct.trim() || pctBps !== null) &&
    (f.scope === "category" ? Boolean(f.categoryId) : Boolean(f.serviceId)) &&
    (f.when === "now" || (at !== null && at.getTime() > Date.now()));

  return (
    <ActionDialog
      wide
      onOpen={() => setF(init())}
      trigger={
        replaces ? (
          <Button size="sm" variant="outline">
            <Repeat /> Değiştir
          </Button>
        ) : (
          <Button size="sm">
            <Plus /> Yeni kural
          </Button>
        )
      }
      title={replaces ? "İptal kuralı: yeni değer" : "Yeni iptal kuralı"}
      description={
        replaces
          ? "Kural yerinde değişmez: yeni değer seçtiğiniz anda başlar, eskisi o anda biter. Talep edilmiş işler kendi kuralını korur."
          : "Bu kategori ya da hizmet için pazarın genel iptal kuralının yerine geçer. Hizmet kuralı kategori kuralından önceliklidir."
      }
      confirmLabel="Kaydet"
      canSubmit={valid}
      success="İptal kuralı kaydedildi."
      onSubmit={async (key) => {
        await api("/v1/admin/cancellation-policies", {
          method: "POST",
          idempotencyKey: key,
          body: {
            marketId: f.marketId,
            categoryId: f.scope === "category" ? f.categoryId : null,
            serviceId: f.scope === "service" ? f.serviceId : null,
            name: f.name.trim() || null,
            freeCancelHours: freeHours,
            cancellationFeeHours: feeN,
            cancellationFeeBps: pctBps,
            effectiveFrom: at ? at.toISOString() : null,
            replacesId: replaces?.id ?? null,
          },
        });
        await qc.invalidateQueries({ queryKey: ["admin-cancellation"] });
      }}
    >
      <div className="grid gap-4">
        {!replaces && (
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="cp-market">Ülke</Label>
              <NativeSelect
                id="cp-market"
                value={f.marketId}
                onChange={(e) => set({ marketId: e.target.value })}
              >
                {options.markets.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cp-scope">Neye uygulanır</Label>
              <NativeSelect
                id="cp-scope"
                value={f.scope}
                onChange={(e) => set({ scope: e.target.value as "category" | "service" })}
              >
                <option value="category">Bir kategori</option>
                <option value="service">Bir hizmet</option>
              </NativeSelect>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cp-target">{f.scope === "category" ? "Kategori" : "Hizmet"}</Label>
              <NativeSelect
                id="cp-target"
                value={f.scope === "category" ? f.categoryId : f.serviceId}
                onChange={(e) =>
                  set(
                    f.scope === "category"
                      ? { categoryId: e.target.value }
                      : { serviceId: e.target.value },
                  )
                }
              >
                <option value="">Seçin</option>
                {(f.scope === "category" ? options.categories : options.services).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="cp-free">Ücretsiz iptal: işe kalan süre</Label>
            <div className="flex gap-2">
              <Input
                id="cp-free"
                inputMode="numeric"
                className="w-28"
                placeholder="ör. 7"
                value={f.free}
                onChange={(e) => set({ free: e.target.value })}
              />
              <NativeSelect
                aria-label="Birim"
                value={f.unit}
                onChange={(e) => set({ unit: e.target.value as "hours" | "days" })}
              >
                <option value="hours">saat</option>
                <option value="days">gün</option>
              </NativeSelect>
            </div>
            <p className="text-xs text-muted-foreground">
              Bundan daha az kala iptal eden müşteriden geç iptal ücreti alınır.
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cp-fee">Geç iptal ücreti (saat ücreti × saat)</Label>
            <Input
              id="cp-fee"
              inputMode="decimal"
              className="w-28"
              placeholder="pazarınki"
              value={f.fee}
              onChange={(e) => set({ fee: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Boş = pazarın genel değeri. Çeyrek saat adımlarıyla (ör. 1,5).
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cp-pct">Sabit fiyatlı işte geç iptal (% tutar)</Label>
            <Input
              id="cp-pct"
              inputMode="decimal"
              className="w-28"
              placeholder="pazarınki"
              value={f.pct}
              onChange={(e) => set({ pct: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Menülü hizmetlerde (ör. manikür) saat yerine tutarın yüzdesi. Boş = pazarınki.
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cp-when">Ne zaman başlasın</Label>
            <NativeSelect
              id="cp-when"
              value={f.when}
              onChange={(e) => set({ when: e.target.value as "now" | "later" })}
            >
              <option value="now">Hemen</option>
              <option value="later">İleri bir tarihte</option>
            </NativeSelect>
          </div>
          {f.when === "later" && (
            <div className="grid gap-2">
              <Label htmlFor="cp-at">Başlangıç</Label>
              <Input
                id="cp-at"
                type="datetime-local"
                value={f.at}
                onChange={(e) => set({ at: e.target.value })}
              />
            </div>
          )}
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="cp-name">Ad (isteğe bağlı)</Label>
            <Input
              id="cp-name"
              maxLength={120}
              placeholder="ör. Etkinlik: 7 gün"
              value={f.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </div>
        </div>
      </div>
    </ActionDialog>
  );
}

function EndDialog({ policy }: { policy: Policy }) {
  const qc = useQueryClient();
  return (
    <ActionDialog
      trigger={
        <Button size="sm" variant="ghost">
          <Square /> Bitir
        </Button>
      }
      title="Kuralı bitir"
      description="Bundan sonra talep edilen işlere genel kural (ya da kategori kuralı) uygulanır. Talep edilmiş işler etkilenmez."
      confirmLabel="Bitir"
      confirmVariant="destructive"
      success="Kural bitirildi."
      onSubmit={async (key) => {
        await api(`/v1/admin/cancellation-policies/${policy.id}/end`, {
          method: "POST",
          idempotencyKey: key,
          body: {},
        });
        await qc.invalidateQueries({ queryKey: ["admin-cancellation"] });
      }}
    />
  );
}

export function CancellationPage() {
  const q = useQuery({
    queryKey: ["admin-cancellation"],
    queryFn: () => api<{ policies: Policy[] }>("/v1/admin/cancellation-policies"),
  });
  const opts = useQuery({
    queryKey: ["admin-cancellation-options"],
    queryFn: () => api<Options>("/v1/admin/cancellation-policies/options"),
  });
  const markets = useQuery({
    queryKey: ["admin-markets"],
    queryFn: () => api<MarketDefaults>("/v1/admin/markets"),
  });
  const [show, setShow] = React.useState<"current" | "all">("current");
  const rows = (q.data?.policies ?? []).filter((p) => show === "all" || p.state !== "ended");

  const columns: ColumnDef<Policy, unknown>[] = [
    {
      id: "scope",
      header: "Kapsam",
      cell: ({ row }) => (
        <div>
          <div className="font-medium">
            {row.original.serviceId
              ? `Hizmet: ${row.original.serviceName}`
              : `Kategori: ${row.original.categoryName}`}
          </div>
          <div className="text-xs text-muted-foreground">
            {row.original.marketCode}
            {row.original.name && ` · ${row.original.name}`}
          </div>
        </div>
      ),
    },
    {
      id: "free",
      header: "Ücretsiz iptal",
      cell: ({ row }) => (
        <span className="font-medium">{hoursText(row.original.freeCancelHours)} kalana kadar</span>
      ),
    },
    {
      id: "fee",
      header: "Geç iptal ücreti",
      cell: ({ row }) => {
        const { cancellationFeeHours: h, cancellationFeeBps: bps } = row.original;
        return (
          <span>
            {h === null ? (
              <span className="text-muted-foreground">pazarınki</span>
            ) : (
              `${h.toString().replace(".", ",")} saat ücreti`
            )}
            {bps !== null && (
              <span className="block text-xs text-muted-foreground">
                sabit fiyatta %{String(bps / 100).replace(".", ",")}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: "period",
      header: "Geçerlilik",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {dateTime(row.original.effectiveFrom)} →{" "}
          {row.original.effectiveTo ? dateTime(row.original.effectiveTo) : "süresiz"}
        </span>
      ),
    },
    {
      id: "state",
      header: "Durum",
      cell: ({ row }) => {
        const [t, v] = STATE[row.original.state];
        return <Badge variant={v}>{t}</Badge>;
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        row.original.state !== "ended" &&
        opts.data && (
          <div className="flex justify-end gap-1">
            <PolicyDialog options={opts.data} replaces={row.original} />
            <EndDialog policy={row.original} />
          </div>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="İptal kuralları"
        description="Kategoriye ya da hizmete özel ücretsiz iptal süresi (ör. etkinliklerde 7 gün). Kural, iş talep edildiği anda işe kopyalanır; sonraki değişiklik açık işleri etkilemez."
        actions={
          <>
            <NativeSelect
              aria-label="Gösterim"
              value={show}
              onChange={(e) => setShow(e.target.value as typeof show)}
            >
              <option value="current">Geçerli ve planlanan</option>
              <option value="all">Geçmiş dahil</option>
            </NativeSelect>
            {opts.data && <PolicyDialog options={opts.data} />}
          </>
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {markets.data?.markets.map((m) => (
          <Card key={m.id}>
            <CardContent className="p-4 text-sm">
              <div className="font-medium">{m.code}: genel kural</div>
              <div className="mt-1 text-muted-foreground">
                {hoursText(Number(m.settings?.free_cancel_hours ?? 24))} kalana kadar ücretsiz;
                sonrasında {String(m.settings?.cancellation_fee_hours ?? 1).replace(".", ",")} saat
                ücreti (sabit fiyatlı işte tutarın %
                {String(Number(m.settings?.cancellation_fee_bps ?? 2500) / 100).replace(".", ",")}
                ).{" "}
                <Link to="/settings" className="text-primary hover:underline">
                  Pazar ayarları
                </Link>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <DataTable
        columns={columns}
        data={q.data ? rows : undefined}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Özel kural yok: tüm hizmetlere genel kural uygulanır."
      />
      <p className="mt-4 text-xs text-muted-foreground">
        Kapora (rezervasyonda alınan ön ödeme) ve sabit fiyatlı işlerde yüzde iptal ücreti henüz
        yok; ödeme akışına eklenince burada görünecek.
      </p>
    </>
  );
}
